import React, { useRef, useState } from "react";
import styled from "styled-components";
import { GlassmorphismContainer } from "../styled/shared/Glassmorphism";
import {
  useCustomAlertStore,
  useSavePathStore,
  useLoadingStore,
} from "../../Store/store";
import { savePath } from "../../api/ProjectAPI";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled(GlassmorphismContainer)`
  width: 440px;
  min-height: 280px;

  color: white;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 20px;
  padding: 24px 28px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  text-align: center;
  color: #fff;
  margin-bottom: 20px;
`;

const Input = styled.input`
  height: 42px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  padding: 0 12px;
  outline: none;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }

  &:focus {
    border-color: #4caf50;
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.25);
  }
`;

const TextArea = styled.textarea`
  height: 80px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  padding: 12px;
  outline: none;
  transition: all 0.2s ease;
  resize: vertical;
  font-family: inherit;

  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }

  &:focus {
    border-color: #4caf50;
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.25);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 10px 18px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 80px;

  &.primary {
    background: #4caf50;
    color: white;

    &:hover {
      background: #45a049;
      transform: translateY(-1px);
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba(255, 255, 255, 0.2);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      color: white;
    }
  }
`;

const Select = styled.select`
  height: 42px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  padding: 0 12px;
  outline: none;
  transition: all 0.2s ease;
  option {
    background: #303231;
    color: #fff;
  }
`;

const SelectLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #fff;
`;

export default function SaveAutoPathModal() {
  const { setIsSaveAutoPathOpen, autoPathData } = useSavePathStore();
  const { showInfoAlert } = useCustomAlertStore();
  const { showLoading, hideLoading } = useLoadingStore();
  const titleRef = useRef(null);
  const [pathTitle, setPathTitle] = useState("");
  const [pathDescription, setPathDescription] = useState("");

  const handleConfirm = async () => {
    try {
      if (pathTitle.trim() === "") {
        showInfoAlert("경로 저장 실패", "경로 제목을 입력해주세요.");
        return;
      }
      if (!autoPathData) return;

      // 로딩 시작
      showLoading("경로 저장 중...");

      const pathData = {
        name: pathTitle,
        description: pathDescription,
        workType: `${autoPathData.workMode.toUpperCase()}_AUTO`,
        agriculturalEquipmentId: `${autoPathData.selectedAutovehicleSpecs}`,
        waypointDetails: autoPathData.waypoints,
        obstacles: [],
        projectId: autoPathData.projectId,
        seedingSpacing: autoPathData.seedingSpacing,
      };

      const response = await savePath(pathData);
      if (response.updatedAt) {
        showInfoAlert("경로 저장 성공", "경로를 저장했습니다.");
        setIsSaveAutoPathOpen(false);
      } else {
        showInfoAlert("경로 저장 실패", "경로를 저장할 수 없습니다.");
      }
    } catch (error) {
      console.error("경로 저장 실패:", error);
      showInfoAlert("경로 저장 실패", "경로를 저장할 수 없습니다.");
    } finally {
      // 로딩 종료
      hideLoading();
      setIsSaveAutoPathOpen(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      setIsSaveAutoPathOpen(false);
    }
  };
  const handleCancel = () => {
    setIsSaveAutoPathOpen(false);
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <Title>경로 저장</Title>
        <Input
          ref={titleRef}
          placeholder="경로 제목을 입력하세요"
          value={pathTitle}
          onChange={(e) => setPathTitle(e.target.value)}
        />
        <TextArea
          placeholder="경로에 대한 간단한 설명을 입력하세요 (선택사항)"
          value={pathDescription}
          onChange={(e) => setPathDescription(e.target.value)}
        />
        <ButtonRow>
          <Button className="secondary" onClick={handleCancel}>
            취소
          </Button>
          <Button className="primary" onClick={handleConfirm}>
            저장
          </Button>
        </ButtonRow>
      </ModalContainer>
    </ModalOverlay>
  );
}
